import {
  WarningOutlined,
  ToolOutlined,
  HistoryOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Button, Typography, Tag, Modal, List, theme } from 'antd';
import React, { useState } from 'react';
import { useMaintanceToDo } from 'providers/MaintanceToDoProvider';
import useTenantFlags from 'hooks/useTenantFlags';
import useMantencionConfig from 'hooks/useMantencionConfig';
import TriState from './mantenciones/TriState';
import MantencionRing from './mantenciones/MantencionRing';
import { MAINT_STATE_COLOR, severidadColor } from './mantenciones/maintenanceColors';
import HallazgoListEditor from './mantenciones/HallazgoListEditor';
import { computeItemGroupProgress } from './mantenciones/itemGroupProgress';
import { DECISION } from 'utils/arrastrePrevios';

// ¿El ítem RECHAZADO está incompleto? Multi-hallazgo: debe tener al menos una
// incidencia con texto, y —si el tenant exige severidad (config
// `hallazgo_severidad` + `severidad_obligatoria`, ver useMantencionConfig)—
// toda incidencia con texto debe tener severidad. `exigeSeveridad` replica la
// misma condición canónica que usa el gate de cierre en MaintanceToDoProvider
// (`mantCfg.severidad && mantCfg.severidadObligatoria`); default true preserva
// el comportamiento previo para callers que no la pasan.
export function isItemIncompleto(
  estado,
  incidencias,
  tienePersistente = false,
  exigeSeveridad = true
) {
  if (estado !== 'RECHAZADO') return false;
  // Un hallazgo previo "sigue presente / cambió" ES la incidencia del ítem (el
  // backend la crea al guardar) → el ítem no está incompleto por esa vía.
  if (tienePersistente) return false;
  const conTexto = (incidencias || []).filter(
    (h) => h.descripcion && String(h.descripcion).trim() !== ''
  );
  if (conTexto.length === 0) return true;
  if (!exigeSeveridad) return false;
  return conTexto.some((h) => !h.severidad);
}

// ¿Hay que auto-generar una fila de incidencia (el campo para describir el
// problema) al marcar el ítem? Un ítem "Con problema" (RECHAZADO) obliga a
// registrar al menos una incidencia; si no tenía ninguna, la creamos vacía para
// que el campo aparezca de inmediato en vez de que el técnico tenga que buscar
// el botón "+ Incidencia".
export function shouldSeedIncidencia(newState, incidenciasCount) {
  return newState === 'RECHAZADO' && (incidenciasCount || 0) === 0;
}

const STATE_LABEL = {
  EXITOSO: 'Exitoso',
  RECHAZADO: 'Con problema',
  NA: 'No aplica',
};

// "3ª visita con este hallazgo · detectado el 12/03"
const historiaPrevio = (h) => {
  const partes = [];
  if (h.mantencionesVistas > 1) partes.push(`${h.mantencionesVistas}ª visita con este hallazgo`);
  if (h.first_seen_at) {
    partes.push(`detectado el ${new Date(h.first_seen_at).toLocaleDateString('es-CL')}`);
  }
  return partes.join(' · ');
};

const Item = ({ Actividad, index, Respuesta, onChangeEstado, idAscensor }) => {
  const { token } = theme.useToken();
  const { isPro } = useTenantFlags();
  const mantCfg = useMantencionConfig();
  const { estado } = Respuesta;
  const {
    addHallazgo,
    updateHallazgo,
    removeHallazgo,
    estados,
    obsSug,
    hallazgosPorActRes,
    saveTick,
    mantencionId,
    decisionesPrevios,
    decidirPrevio,
    deshacerDecisionPrevio,
    limpiarArrastresDelItem,
  } = useMaintanceToDo();

  const [showHistorial, setShowHistorial] = useState(false);
  // tempId de la incidencia sembrada al marcar "Con problema": la fila se enfoca
  // sola. No aplica al botón "+ Incidencia" (ahí el técnico ya sabe dónde está).
  const [focusTempId, setFocusTempId] = useState(null);

  // Estado efectivo: override del provider (sobrevive remounts) sobre el fetch.
  const overrideEstado = estados?.[Respuesta.id];
  const state = overrideEstado !== undefined ? overrideEstado : estado;
  const isRechazado = state === 'RECHAZADO';

  // Listas de hallazgos del ítem (source of truth: obsSug del provider).
  const item = obsSug?.[Respuesta.id];
  const incidencias = item?.incidencias || [];
  const sugerencias = item?.sugerencias || [];

  // Hallazgos previos del ítem (arrastre 96.0): el técnico decide por-hallazgo
  // desde acá ("Resuelto" / "Sigue presente", ver botones más abajo) — el
  // backend ya no deriva la decisión del estado del ítem, por eso existen
  // estos botones.
  const hallazgosPrevios = Respuesta?.hallazgosPrevios ?? [];

  const exigeSeveridad = mantCfg.severidad && mantCfg.severidadObligatoria;
  const incompleto = isItemIncompleto(state, incidencias, false, exigeSeveridad);

  // Detalle legacy (migración): antes del modelo multi-hallazgo el problema del
  // ítem se guardaba en `observacion`. El checklist nuevo hidrata sólo hallazgos
  // ACTIVA, así que un ítem migrado se ve vacío aunque tenga este texto. Si está
  // RECHAZADO, no tiene incidencias vivas y hay `observacion`, lo ofrecemos para
  // convertirlo en incidencia de un toque (así se puede completar y cerrar).
  const legacyObs = String(Respuesta.observacion || '').trim();
  const mostrarLegacyObs = isRechazado && incidencias.length === 0 && legacyObs !== '';
  const usarObservacionComoIncidencia = () => {
    const tempId = addHallazgo(Respuesta.id, 'INCIDENCIA');
    if (tempId) updateHallazgo(Respuesta.id, 'INCIDENCIA', tempId, { descripcion: legacyObs });
  };

  // Quitar una incidencia con la X: si es la fila arrastrada de un hallazgo
  // previo (tiene `idHallazgoPrevio`), quitarla con la X tiene que equivaler a
  // "Deshacer" esa decisión — si no, la tarjeta del previo sigue mostrando
  // "Sigue presente" mientras la incidencia real ya no existe (se ve una cosa,
  // se guarda otra). Cualquier otra incidencia se quita como siempre.
  const onRemoveHallazgo = (tipo, tempId) => {
    if (tipo === 'INCIDENCIA') {
      const arrastrada = incidencias.find((h) => h.tempId === tempId);
      if (arrastrada?.idHallazgoPrevio != null) {
        setFocusTempId(null);
        deshacerDecisionPrevio(Respuesta.id, arrastrada.idHallazgoPrevio);
        return;
      }
    }
    removeHallazgo(Respuesta.id, tipo, tempId);
  };

  const aplicarEstado = (newState) => {
    onChangeEstado(Respuesta.id, newState);
    // Al pasar a "Con problema" generamos la primera incidencia y la enfocamos:
    // el sistema exige registrarla, así que no dejamos al técnico buscando el
    // botón "+ Incidencia" ni el campo de descripción.
    if (shouldSeedIncidencia(newState, incidencias.length)) {
      setFocusTempId(addHallazgo(Respuesta.id, 'INCIDENCIA') ?? null);
    } else {
      setFocusTempId(null);
    }
  };

  // ¿Hay algún hallazgo previo de este ítem marcado "Sigue presente"? Si el
  // técnico reclasifica el ítem a Exitoso/No aplica, o lo deselecciona (vuelve
  // a tocar "Con problema" ya activo → TriState emite null), con un arrastre
  // activo, esa marca deja de tener sentido — se avisa antes de deshacerla,
  // nunca se bloquea la reclasificación.
  const hayArrastresActivos = hallazgosPrevios.some(
    (h) => decisionesPrevios?.[h.id] === DECISION.SIGUE
  );

  const onChangeEstadoHandler = (newState) => {
    // Cualquier salida de "Con problema" (Exitoso, No aplica o deseleccionar)
    // deshace el arrastre. El único destino que lo conserva es quedarse en
    // RECHAZADO, y ese no llega acá: si ya está activo, TriState toggle-a a
    // null en vez de reenviar 'RECHAZADO'.
    if (hayArrastresActivos && newState !== 'RECHAZADO') {
      Modal.confirm({
        title: 'El hallazgo anterior vuelve a quedar sin confirmar',
        content:
          'Marcaste que el hallazgo de la visita anterior sigue presente. Si cambias el estado de este ítem, esa marca se deshace y el hallazgo queda pendiente para la próxima visita.',
        okText: 'Cambiar igual',
        cancelText: 'Volver',
        onOk: () => {
          limpiarArrastresDelItem(Respuesta.id, hallazgosPrevios);
          aplicarEstado(newState);
        },
      });
      return;
    }
    aplicarEstado(newState);
  };

  // Obtener hallazgos activos del historial
  const getActiveHallazgos = () => {
    const historial = Respuesta?.historialHallazgos;
    if (!historial) return { sugerencias: [], incidencias: [] };
    return {
      sugerencias: historial.sugerencias?.activas || [],
      incidencias: historial.incidencias?.activas || [],
    };
  };

  const activeHallazgos = getActiveHallazgos();
  const hasActiveHallazgos =
    activeHallazgos.sugerencias.length > 0 || activeHallazgos.incidencias.length > 0;

  const reincidenciaCount = Respuesta?.reincidencia_count ?? 0;
  const hasReincidencia = reincidenciaCount >= 3;

  const stateColor = state ? MAINT_STATE_COLOR[state] : token.colorBorder;

  return (
    <div
      id={`actividad-respuesta-${Respuesta.id}`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {/* Barra lateral de color-estado */}
      <div
        aria-hidden="true"
        style={{
          width: 4,
          flexShrink: 0,
          background: stateColor,
          transition: `background ${token.motionDurationMid}`,
        }}
      />

      {/* Contenido del ítem */}
      <div style={{ minWidth: 0, flex: 1, padding: '16px 16px 18px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span
            className="mono"
            style={{
              display: 'flex',
              height: 28,
              width: 28,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: token.borderRadius,
              fontSize: 12,
              fontWeight: 700,
              background: state ? `${stateColor}1A` : token.colorFillTertiary,
              color: state ? stateColor : token.colorTextTertiary,
              transition: `all ${token.motionDurationMid}`,
            }}
          >
            {index + 1}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.35,
                color: token.colorText,
              }}
            >
              {Actividad.actividad}
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 6,
              }}
            >
              {state && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    borderRadius: 999,
                    padding: '1px 8px',
                    fontSize: 11,
                    fontWeight: 500,
                    background: `${stateColor}1A`,
                    color: stateColor,
                  }}
                >
                  {STATE_LABEL[state]}
                </span>
              )}
              {incompleto && (
                <Tag color="warning" style={{ margin: 0 }}>
                  Incompleto
                </Tag>
              )}
            </div>
          </div>
          {hasReincidencia && (
            <span
              style={{
                position: 'relative',
                flexShrink: 0,
                borderRadius: token.borderRadius,
                padding: 8,
                background: token.colorErrorBg,
                color: token.colorError,
              }}
              title={`Este ítem fue rechazado ${reincidenciaCount} veces en este ascensor en los últimos 6 meses`}
            >
              <WarningOutlined style={{ fontSize: 16 }} />
              <span
                style={{
                  position: 'absolute',
                  right: -4,
                  top: -4,
                  display: 'flex',
                  minWidth: 18,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 999,
                  padding: '0 4px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: token.colorWhite,
                  background: token.colorError,
                  boxShadow: `0 0 0 2px ${token.colorBgContainer}`,
                }}
              >
                {reincidenciaCount}
              </span>
            </span>
          )}
          {hasActiveHallazgos && (
            <button
              type="button"
              onClick={() => setShowHistorial(true)}
              style={{
                position: 'relative',
                flexShrink: 0,
                borderRadius: token.borderRadius,
                padding: 8,
                border: 0,
                background: 'transparent',
                color: token.colorWarning,
                cursor: 'pointer',
              }}
              title="Ver historial de hallazgos activos"
            >
              <HistoryOutlined style={{ fontSize: 16 }} />
              <span
                style={{
                  position: 'absolute',
                  right: 4,
                  top: 4,
                  height: 8,
                  width: 8,
                  borderRadius: 999,
                  background: token.colorWarning,
                  boxShadow: `0 0 0 2px ${token.colorBgContainer}`,
                }}
              />
            </button>
          )}
        </div>

        {/* Hallazgos de visitas anteriores en este ítem (arrastre 96.0): el
            técnico decide por hallazgo — "Resuelto" cierra el previo, "Sigue
            presente" arrastra una incidencia precargada y pasa el ítem a "Con
            problema". Si no decide, el hallazgo queda pendiente y vuelve a
            aparecer en la próxima visita. */}
        {hallazgosPrevios.length > 0 && (
          <div
            style={{
              marginTop: 14,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorInfoBorder}`,
              background: token.colorInfoBg,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: token.colorText }}>
              <HistoryOutlined style={{ marginRight: 6 }} />
              Hallazgos de visitas anteriores en este ítem
            </div>

            {hallazgosPrevios.map((h) => {
              const decision = decisionesPrevios?.[h.id];
              return (
                <div
                  key={h.id}
                  style={{
                    border: `1px dashed ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    padding: 10,
                    background: token.colorBgContainer,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div>
                    <Tag color={severidadColor(h.severidad)} style={{ marginRight: 6 }}>
                      {h.severidad || 'MEDIA'}
                    </Tag>
                    <Typography.Text style={{ fontSize: 13 }}>{h.descripcion}</Typography.Text>
                  </div>

                  {historiaPrevio(h) && (
                    <div style={{ fontSize: 12, color: token.colorTextTertiary }}>
                      {historiaPrevio(h)}
                    </div>
                  )}

                  {decision ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          color:
                            decision === DECISION.RESUELTO
                              ? token.colorSuccess
                              : token.colorWarning,
                        }}
                      >
                        {decision === DECISION.RESUELTO ? <CheckOutlined /> : <WarningOutlined />}
                        {decision === DECISION.RESUELTO
                          ? 'Marcado como resuelto'
                          : 'Sigue presente'}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setFocusTempId(null);
                          deshacerDecisionPrevio(Respuesta.id, h.id);
                        }}
                      >
                        Deshacer
                      </Button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => {
                          setFocusTempId(null);
                          decidirPrevio(Respuesta.id, h, DECISION.RESUELTO);
                        }}
                        style={{ color: token.colorSuccess, borderColor: token.colorSuccessBorder }}
                      >
                        Resuelto
                      </Button>
                      <Button
                        size="small"
                        icon={<WarningOutlined />}
                        onClick={() =>
                          setFocusTempId(decidirPrevio(Respuesta.id, h, DECISION.SIGUE) ?? null)
                        }
                        style={{ color: token.colorWarning, borderColor: token.colorWarningBorder }}
                      >
                        Sigue presente
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ fontSize: 12.5, color: token.colorTextTertiary }}>
              Si no lo confirmas, el hallazgo queda pendiente y vuelve a aparecer en la próxima
              visita.
            </div>
          </div>
        )}

        {/* Estado — control 3-estados. Cambiar de estado (incluso deseleccionar)
            es siempre válido, aunque el ítem tenga un arrastre activo: nunca se
            bloquea al técnico acá — `onChangeEstadoHandler` avisa (Modal.confirm)
            y deshace el arrastre si corresponde, el backend ya no lo deriva
            solo. */}
        <TriState value={state} onPick={(v) => onChangeEstadoHandler(v)} />

        {/* Detalle legacy migrado: contexto + convertir en incidencia de un toque. */}
        {mostrarLegacyObs && (
          <div
            style={{
              marginTop: 12,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorWarningBorder}`,
              background: token.colorWarningBg,
              padding: 12,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: token.colorWarningText }}>
              Detalle registrado anteriormente
            </div>
            <p style={{ margin: '4px 0 8px', fontSize: 13, color: token.colorText }}>{legacyObs}</p>
            <Button size="small" type="primary" onClick={usarObservacionComoIncidencia}>
              Usar como incidencia
            </Button>
          </div>
        )}

        {/* Lista de hallazgos (incidencias si RECHAZADO + sugerencias en cualquier
            estado). Variante B: filas densas, fotos por hallazgo. */}
        {state && (
          <div style={{ marginTop: 16 }}>
            <HallazgoListEditor
              incidencias={incidencias}
              sugerencias={sugerencias}
              autoFocusTempId={focusTempId}
              showIncidencias={isRechazado}
              showSugerencias={mantCfg.sugerencias}
              showFotos={mantCfg.fotos}
              showSeveridad={mantCfg.severidad}
              exigeSeveridad={exigeSeveridad}
              onAdd={(tipo) => addHallazgo(Respuesta.id, tipo)}
              onUpdate={(tipo, tempId, patch) => updateHallazgo(Respuesta.id, tipo, tempId, patch)}
              onRemove={onRemoveHallazgo}
              mantencionId={mantencionId}
              hallazgosPorActRes={hallazgosPorActRes}
              isPro={isPro}
              equipoId={idAscensor}
              saveTick={saveTick}
            />
          </div>
        )}
      </div>

      {/* Modal de Historial de Hallazgos */}
      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <HistoryOutlined style={{ marginRight: 8, color: token.colorWarning }} />
            Historial de Hallazgos Activos
          </span>
        }
        open={showHistorial}
        onCancel={() => setShowHistorial(false)}
        footer={[
          <Button key="close" onClick={() => setShowHistorial(false)}>
            Cerrar
          </Button>,
        ]}
        width={800}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Sugerencias Activas */}
          {activeHallazgos.sugerencias.length > 0 && (
            <div>
              <Typography.Title
                level={5}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}
              >
                <ToolOutlined style={{ marginRight: 8, color: token.colorSuccess }} />
                Sugerencias Activas ({activeHallazgos.sugerencias.length})
              </Typography.Title>
              <List
                dataSource={activeHallazgos.sugerencias}
                renderItem={(sugerencia) => (
                  <List.Item
                    style={{
                      border: `1px solid ${token.colorSuccessBorder}`,
                      borderRadius: token.borderRadiusLG,
                      padding: 16,
                      marginBottom: 12,
                      background: token.colorSuccessBg,
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                        }}
                      >
                        <Tag color="green">SUGERENCIA</Tag>
                      </div>
                      <Typography.Text>{sugerencia.descripcion}</Typography.Text>
                      {sugerencia.observaciones && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            <strong>Observaciones:</strong> {sugerencia.observaciones}
                          </Typography.Text>
                        </div>
                      )}
                      <div style={{ marginTop: 8, fontSize: 12, color: token.colorTextTertiary }}>
                        Creado: {new Date(sugerencia.createdAt).toLocaleDateString('es-CL')}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Incidencias Activas */}
          {activeHallazgos.incidencias.length > 0 && (
            <div>
              <Typography.Title
                level={5}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}
              >
                <WarningOutlined style={{ marginRight: 8, color: token.colorError }} />
                Incidencias Activas ({activeHallazgos.incidencias.length})
              </Typography.Title>
              <List
                dataSource={activeHallazgos.incidencias}
                renderItem={(incidencia) => (
                  <List.Item
                    style={{
                      border: `1px solid ${token.colorErrorBorder}`,
                      borderRadius: token.borderRadiusLG,
                      padding: 16,
                      marginBottom: 12,
                      background: token.colorErrorBg,
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                        }}
                      >
                        <Tag color="red">INCIDENCIA</Tag>
                        <Tag color={severidadColor(incidencia.severidad)}>
                          {incidencia.severidad}
                        </Tag>
                      </div>
                      <Typography.Text>{incidencia.descripcion}</Typography.Text>
                      {incidencia.observaciones && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            <strong>Observaciones:</strong> {incidencia.observaciones}
                          </Typography.Text>
                        </div>
                      )}
                      <div style={{ marginTop: 8, fontSize: 12, color: token.colorTextTertiary }}>
                        Creado: {new Date(incidencia.createdAt).toLocaleDateString('es-CL')}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Mensaje si no hay hallazgos activos */}
          {!hasActiveHallazgos && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <HistoryOutlined
                style={{ fontSize: 36, color: token.colorTextQuaternary, marginBottom: 12 }}
              />
              <div>
                <Typography.Text type="secondary">
                  No hay hallazgos activos para esta actividad
                </Typography.Text>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

function MaintanceToDoItemList({ id, item, estados, onChangeEstado, idAscensor }) {
  const { token } = theme.useToken();
  const { actividades } = item;
  const [open, setOpen] = useState(true);
  const [showHidden, setShowHidden] = useState(false);
  const visibleActividades = actividades?.filter((a) => a.Respuesta.visible || showHidden);

  // El anillo refleja solo ítems visibles; la alerta (rojo) es solo por ítems
  // sin estado, NO por "con problema" (que es información, no error).
  const {
    done,
    total: totalOperables,
    faltan,
    conProblema,
  } = computeItemGroupProgress(actividades, estados);

  return (
    <div
      id={id}
      style={{
        margin: '10px 0 0',
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        overflow: 'hidden',
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          width: '100%',
          padding: '12px 13px',
          border: 0,
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <MantencionRing done={done} total={totalOperables} alert={faltan > 0} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: token.colorText }}>{item.nombre}</div>
          <div
            style={{
              fontSize: 11.5,
              color: token.colorTextTertiary,
              display: 'flex',
              gap: 8,
            }}
          >
            <span>{actividades?.length} actividades</span>
            {faltan > 0 && (
              <span style={{ color: token.colorError, fontWeight: 600 }}>{faltan} sin marcar</span>
            )}
            {conProblema > 0 && (
              <span style={{ color: token.colorInfo, fontWeight: 600 }}>
                {conProblema} con problema
              </span>
            )}
          </div>
        </div>
        <span
          style={{
            color: token.colorTextTertiary,
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform .25s',
          }}
        >
          ›
        </span>
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}>
          {/* Mostrar/ocultar actividades ocultas */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '8px 13px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Button
              type="text"
              size="small"
              icon={showHidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => setShowHidden((s) => !s)}
              title={showHidden ? 'Ocultar ocultas' : 'Mostrar ocultas'}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextTertiary,
              }}
            >
              {showHidden ? 'Ocultar' : 'Mostrar'} ocultas
            </Button>
          </div>
          {visibleActividades?.map(({ Respuesta, Actividad }, i) => (
            <Item
              key={Respuesta.id}
              index={i}
              Actividad={Actividad}
              Respuesta={Respuesta}
              idAscensor={idAscensor}
              onChangeEstado={onChangeEstado}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default MaintanceToDoItemList;
